import React, { useState, useEffect, useCallback } from 'react';
import AiSuggestionPanel from './AiSuggestionPanel';
import api from '../../lib/api';
import { aiAPI } from '../../lib/api';

/**
 * DataAssistant – appears on the Review Queue after transactions are loaded.
 * Analyzes transaction data for duplicates, VAT issues, uncertain classifications.
 *
 * Props:
 *  - transactions: array of transaction objects
 *  - onHighlight: (action, ids) => void – callback to highlight specific transactions
 */
export default function DataAssistant({ transactions = [], onHighlight }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    if (hasRequested || transactions.length === 0) return;
    setHasRequested(true);
    setLoading(true);

    // Build a compact summary for the AI
    const duplicates = transactions.filter(t => t.is_duplicate).length;
    const lowConfidence = transactions.filter(t => (t.ai_confidence || 0) < 0.7).length;
    const noVat = transactions.filter(t => !t.tax_rate && t.amount < 0).length;
    const matched = transactions.filter(t => t.matched_invoice_id).length;
    const total = transactions.length;

    const summary = {
      total_transactions: total,
      potential_duplicates: duplicates,
      low_confidence_classifications: lowConfidence,
      missing_vat: noVat,
      invoice_matched: matched,
      sample_descriptions: transactions.slice(0, 5).map(t => ({
        description: t.description?.substring(0, 60),
        amount: t.amount,
        confidence: t.ai_confidence,
        account: t.suggested_account_name,
      })),
    };

    try {
      const res = await api.post('/ai/suggestions', {
        context_type: 'data_review',
        payload: summary,
      });
      setSuggestions(res.data || []);
    } catch {
      setSuggestions([]);
    }
    setLoading(false);
  }, [transactions, hasRequested]);

  useEffect(() => {
    if (transactions.length >= 3 && !hasRequested) {
      // Small delay to not block initial render
      const t = setTimeout(fetchSuggestions, 1500);
      return () => clearTimeout(t);
    }
  }, [transactions.length, fetchSuggestions, hasRequested]);

  const handleAccept = (suggestion) => {
    // Store preference for learning
    aiAPI.acceptSuggestion('data_review', 'data_review_action', suggestion.text).catch(() => {});
    if (suggestion.action && onHighlight) {
      onHighlight(suggestion.action, suggestion.data);
    }
  };

  if (!hasRequested && transactions.length < 3) return null;

  return (
    <AiSuggestionPanel
      title="AI Review Summary"
      suggestions={suggestions}
      loading={loading}
      onAccept={handleAccept}
      onDismiss={() => setSuggestions([])}
      className="mb-4"
    />
  );
}
